import type { AudioAsset, AudioStatus, Language, Row } from '@tekvoice/shared';

/** Statuses a text edit may promote to STALE — never an in-flight or failed one. */
const STALEABLE: AudioStatus[] = ['GENERATED', 'APPROVED', 'STALE'];

/**
 * SPEC §3 — a GENERATED asset whose text changed is not "generated" to the user:
 * it is stale ("text changed — regenerate") and approval is blocked. The stored
 * `status` only flips to STALE when the asset was APPROVED, so the UI derives it.
 *
 * Only SETTLED statuses are promoted, matching `BatchesService.effectiveStatus`: a chip
 * reading "stale" for an asset the worker is generating right now contradicts the run
 * progress bar, and one reading "stale" for a FAILED asset contradicts the same row's
 * enabled "Retry failed" button and the export dialog's failed count.
 */
export function effectiveStatus(asset: AudioAsset | null): AudioStatus | null {
  if (!asset) return null;
  return asset.isStale && STALEABLE.includes(asset.status) ? 'STALE' : asset.status;
}

export const STATUS_LABELS: Record<AudioStatus, string> = {
  PENDING: 'pending',
  GENERATING: 'generating',
  FAILED: 'failed',
  GENERATED: 'generated',
  APPROVED: 'approved',
  STALE: 'stale',
};

/** Row-level text for a language, honouring the CSV columns. */
export function textFor(row: Row, language: Language): string {
  return language === 'EN' ? row.englishText : row.arabicText;
}

export function overrideFor(row: Row, language: Language): string | null {
  return language === 'EN' ? row.spokenOverrideEn : row.spokenOverrideAr;
}

/** What is actually sent to TTS (SPEC §3). */
export function spokenTextFor(row: Row, language: Language): string {
  return overrideFor(row, language) ?? textFor(row, language);
}

/** An asset that has never produced a take and still has text to speak. */
export function isMissing(row: Row, asset: AudioAsset | null): boolean {
  if (!asset) return false;
  if (asset.currentTakeId !== null) return false;
  return textFor(row, asset.language).trim().length > 0;
}

export function isFailed(asset: AudioAsset | null): boolean {
  return asset?.status === 'FAILED';
}

/**
 * C8 — a single-asset regeneration in flight. It is a SEPARATE signal from `status`:
 * an APPROVED asset stays APPROVED (and keeps exporting its published URL) while the
 * replacement take is produced, so the chip has to render both — "approved" plus a
 * "regenerating" marker — instead of the status silently reading "generating".
 */
export function isRegenerating(asset: AudioAsset | null): boolean {
  return Boolean(asset?.regeneratingAt);
}

/**
 * m4 — the per-row Regenerate button. A PENDING asset is only off-limits while a run
 * actually owns it; a PENDING asset with no live run (an abandoned regeneration, an
 * aborted run) must be reachable or it is stuck with no way back.
 */
export function canRegenerateAsset(asset: AudioAsset | null, activeRunId: string | null): boolean {
  if (!asset) return false;
  if (isRegenerating(asset)) return false;
  if (asset.status === 'GENERATING') return false;
  if (asset.status === 'PENDING') return activeRunId === null || asset.runId !== activeRunId;
  return true;
}

/** Blank arabic_text is allowed and simply skipped by runs (SPEC §5). */
export function isSkipped(row: Row, asset: AudioAsset | null): boolean {
  if (!asset) return true;
  return textFor(row, asset.language).trim().length === 0;
}

export function rowAssets(row: Row): AudioAsset[] {
  return [row.assets.EN, row.assets.AR].filter((asset): asset is AudioAsset => asset !== null);
}

/**
 * SPEC §7 — approve requires both assets GENERATED (or re-approvable APPROVED),
 * neither stale, and non-empty text on both sides.
 */
export function isApprovable(row: Row): boolean {
  if (row.removedAt) return false;
  if (row.englishText.trim().length === 0 || row.arabicText.trim().length === 0) return false;
  const assets = [row.assets.EN, row.assets.AR];
  return assets.every((asset) => {
    if (!asset) return false;
    if (asset.isStale) return false;
    return asset.status === 'GENERATED' || asset.status === 'APPROVED';
  });
}

export function isFullyApproved(row: Row): boolean {
  const assets = [row.assets.EN, row.assets.AR];
  return assets.every((asset) => asset !== null && asset.status === 'APPROVED' && !asset.isStale);
}

export function rowIsStale(row: Row): boolean {
  return rowAssets(row).some((asset) => asset.isStale || asset.status === 'STALE');
}

/** Does this row match a status filter chip? (Either language counts.) */
export function rowMatchesStatus(row: Row, status: AudioStatus): boolean {
  return rowAssets(row).some((asset) => effectiveStatus(asset) === status);
}

export type StatusFilter = 'all' | AudioStatus;

export const STATUS_FILTERS: StatusFilter[] = [
  'all',
  'PENDING',
  'GENERATING',
  'FAILED',
  'GENERATED',
  'APPROVED',
  'STALE',
];

/**
 * TODO-CONTRACT: `filterCountsSchema` exists in @tekvoice/shared but no endpoint
 * returns it, and `batchDetail.statusCounts` counts assets, not rows. The chips
 * count rows, so they are derived from the loaded page here — which also keeps
 * them live while rows are edited without a refetch.
 */
export function countByStatus(rows: Row[]): Record<StatusFilter, number> {
  const counts: Record<StatusFilter, number> = {
    all: rows.length,
    PENDING: 0,
    GENERATING: 0,
    FAILED: 0,
    GENERATED: 0,
    APPROVED: 0,
    STALE: 0,
  };
  for (const row of rows) {
    for (const status of STATUS_FILTERS) {
      if (status === 'all') continue;
      if (rowMatchesStatus(row, status)) counts[status] += 1;
    }
  }
  return counts;
}
